import Image from 'next/image';

import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import {
  successStoriesContent,
  successStoriesItems,
  SuccessStory,
  SuccessStoryTheme,
} from '~/features/government/configs/success-stories-content';

type ThemeTokens = {
  cardBg: string;
  cardBorder: string;
  quoteBorder: string;
  statBg: string;
  statValueColor: string;
  statIconBg: string;
};

const THEME_TOKENS: Record<SuccessStoryTheme, ThemeTokens> = {
  blue: {
    cardBg: 'blue.50',
    cardBorder: 'blue.100',
    quoteBorder: 'blue.100',
    statBg: 'blue.900',
    statValueColor: '#AFC6FF',
    statIconBg: 'rgba(123, 162, 255, 0.2)',
  },
  purple: {
    cardBg: 'purple.50',
    cardBorder: 'purple.100',
    quoteBorder: 'purple.100',
    statBg: '#3A006A',
    statValueColor: '#EBBAFF',
    statIconBg: 'whiteAlpha.200',
  },
  orange: {
    cardBg: 'orange.50',
    cardBorder: 'orange.200',
    quoteBorder: 'orange.200',
    statBg: 'orange.900',
    statValueColor: 'orange.200',
    statIconBg: 'whiteAlpha.200',
  },
  grey: {
    cardBg: 'grey.50',
    cardBorder: 'grey.200',
    quoteBorder: 'grey.200',
    statBg: 'grey.900',
    statValueColor: 'white',
    statIconBg: 'whiteAlpha.200',
  },
};

export type GovernmentSuccessStoriesProps = {
  heading?: string;
  description?: string;
  stories?: SuccessStory[];
};

export function GovernmentSuccessStories({
  heading = successStoriesContent.heading,
  description = successStoriesContent.description,
  stories = successStoriesItems,
}: GovernmentSuccessStoriesProps) {
  // Top padding is dropped on desktop because this white section sits directly below the
  // white Societal Impact section (which already contributes 104px bottom padding);
  // without this the matching backgrounds read as one oversized gap.
  return (
    <Flex
      as='section'
      bg='white'
      flexDirection='column'
      gap={{ base: '40px', md: '80px' }}
      pb={{ base: '56px', md: '104px' }}
      pt={{ base: '56px', md: '0' }}
    >
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Stack
          alignItems='center'
          margin='0 auto'
          maxW={{ base: '100%', md: '850px' }}
          spacing={{ base: '16px', md: '24px' }}
          textAlign='center'
        >
          <Heading
            as='h2'
            color='grey.900'
            fontFamily='heading'
            fontSize={{ base: '2.375rem', md: '3.375rem' }}
            fontWeight='600'
            letterSpacing='-0.02em'
            lineHeight='1.1'
          >
            {heading}
          </Heading>
          <Text
            color='grey.500'
            fontSize={{ base: '0.875rem', md: '1rem' }}
            letterSpacing={{ base: '-0.01em', md: 'normal' }}
            lineHeight={{ base: '1.4', md: '1.5' }}
          >
            {description}
          </Text>
        </Stack>
      </ContentContainer>

      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex
          flexDirection='column'
          gap={{ base: '16px', md: '40px' }}
          margin='0 auto'
          maxW={{ base: '100%', md: '1096px' }}
        >
          {stories.map((story) =>
            story.kind === 'feature' ? (
              <FeatureStoryCard key={story.id} story={story} />
            ) : (
              <QuoteStoryCard key={story.id} story={story} />
            )
          )}
        </Flex>
      </ContentContainer>
    </Flex>
  );
}

function FeatureStoryCard({ story }: { story: Extract<SuccessStory, { kind: 'feature' }> }) {
  const tokens = THEME_TOKENS[story.theme];
  const imageOnRight = story.imageSide === 'right';

  return (
    <Flex
      bg={tokens.cardBg}
      border='1.33px solid'
      borderColor={tokens.cardBorder}
      borderRadius={{ base: '12px', md: '16px' }}
      direction={{ base: 'column', md: imageOnRight ? 'row-reverse' : 'row' }}
      gap={{ base: '16px', md: '40px' }}
      p={{ base: '16px', md: '24px' }}
    >
      {/* Desktop-only left/right photo card with gradient name overlay */}
      <Box display={{ base: 'none', md: 'block' }}>
        <PersonImage story={story} />
      </Box>

      <Stack flex='1' minW={0} spacing={{ base: '16px', md: '24px' }}>
        <Stack p={{ base: '8px', md: '0' }} pt={{ md: '8px' }} spacing={{ base: '12px', md: '16px' }}>
          <Heading
            as='h3'
            color='grey.900'
            fontFamily='heading'
            fontSize={{ base: '1.75rem', md: '2.25rem' }}
            fontWeight='600'
            letterSpacing={{ base: '-0.01em', md: '-0.01em' }}
            lineHeight={{ base: '1.15', md: '1.1' }}
          >
            {story.heading}
          </Heading>
          {story.description && (
            <Text
              color={{ base: 'grey.500', md: 'grey.700' }}
              fontSize={{ base: '0.875rem', md: '1rem' }}
              letterSpacing={{ base: '-0.01em', md: 'normal' }}
              lineHeight={{ base: '1.4', md: '1.5' }}
            >
              {story.description}
            </Text>
          )}
        </Stack>

        <StatBlock
          caption={story.stat.caption}
          flag={story.flag}
          iconSrc={story.stat.iconSrc}
          tokens={tokens}
          value={story.stat.value}
          valueSuffix={story.stat.valueSuffix}
        />

        {/* Mobile-only: image + byline side-by-side row */}
        <Flex display={{ base: 'flex', md: 'none' }} gap='16px' w='100%'>
          <Box borderRadius='6px' flex='1' h='200px' minW='0' overflow='hidden' position='relative'>
            <Image
              alt={story.person.name}
              sizes='(max-width: 768px) 50vw, 166px'
              src={story.person.imageSrc}
              style={{
                objectFit: 'cover',
                objectPosition: story.person.imagePosition ?? 'center',
              }}
              fill
            />
          </Box>
          <Flex direction='column' flexShrink={0} justifyContent='flex-end' w='144px'>
            <MobilePersonByline person={story.person} />
          </Flex>
        </Flex>

        <QuoteCard borderColor={tokens.quoteBorder}>{story.quote}</QuoteCard>
      </Stack>
    </Flex>
  );
}

function PersonImage({ story }: { story: Extract<SuccessStory, { kind: 'feature' }> }) {
  return (
    <Box
      bg='grey.900'
      borderRadius='8px'
      flexShrink={0}
      height='auto'
      minHeight='384px'
      overflow='hidden'
      position='relative'
      width='344px'
    >
      <Image
        alt={story.person.name}
        sizes='344px'
        src={story.person.imageSrc}
        style={{
          objectFit: 'cover',
          objectPosition: story.person.imagePosition ?? 'center',
        }}
        fill
      />
      <Stack
        background='linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)'
        bottom='0'
        color='white'
        left='0'
        pb='24px'
        position='absolute'
        pt='40px'
        px='24px'
        right='0'
        spacing='4px'
      >
        <Text fontFamily='heading' fontSize='1.25rem' fontWeight='600' letterSpacing='-0.01em' lineHeight='1.2'>
          {story.person.name}
        </Text>
        <Text fontSize='0.8125rem' fontWeight='500' lineHeight='1.4'>
          {story.person.title}
        </Text>
      </Stack>
    </Box>
  );
}

function MobilePersonByline({ person }: { person: Extract<SuccessStory, { kind: 'feature' }>['person'] }) {
  return (
    <Stack spacing='4px'>
      <Text
        color='grey.900'
        fontFamily='heading'
        fontSize='1.125rem'
        fontWeight='600'
        letterSpacing='-0.01em'
        lineHeight='1.2'
      >
        {person.name}
      </Text>
      <Text color='grey.700' fontSize='0.75rem' lineHeight='1.4'>
        {person.title}
      </Text>
    </Stack>
  );
}

function StatBlock({
  caption,
  flag,
  iconSrc,
  tokens,
  value,
  valueSuffix,
}: {
  caption: string;
  flag: { src: string; alt: string };
  iconSrc: string;
  tokens: ThemeTokens;
  value: string;
  valueSuffix?: string;
}) {
  return (
    <Stack
      bg={tokens.statBg}
      borderRadius='5px'
      maxW={{ base: '100%', md: '364px' }}
      p={{ base: '16px', md: '20px' }}
      spacing='8px'
      w='100%'
    >
      <Flex alignItems='center' justifyContent='space-between' w='100%'>
        <Flex alignItems='center' gap='16px'>
          <Flex
            alignItems='center'
            bg={tokens.statIconBg}
            borderRadius='4px'
            h='40px'
            justifyContent='center'
            position='relative'
            w='40px'
          >
            <Image alt='' height={26} src={iconSrc} style={{ transform: 'scaleY(-1)' }} width={26} />
          </Flex>
          <Box color={tokens.statValueColor} fontFamily='heading' fontWeight='600' lineHeight='1' whiteSpace='nowrap'>
            <Text as='span' fontSize='2.25rem' lineHeight='1'>
              {value}
            </Text>
            {valueSuffix && (
              <Text as='span' fontSize={{ base: '1.75rem', md: '1.5625rem' }} lineHeight='1'>
                {valueSuffix}
              </Text>
            )}
          </Box>
        </Flex>
        <Box flexShrink={0} h='32px' position='relative' w='48px'>
          <Image alt={flag.alt} sizes='48px' src={flag.src} style={{ objectFit: 'contain' }} fill />
        </Box>
      </Flex>
      <Text color='white' fontSize={{ base: '0.75rem', md: '0.8125rem' }} lineHeight='1.4'>
        {caption}
      </Text>
    </Stack>
  );
}

function QuoteCard({ borderColor, children }: { borderColor: string; children: string }) {
  return (
    <Box
      bg='white'
      border='1px solid'
      borderColor={borderColor}
      borderRadius='8px'
      p={{ base: '16px', md: '24px' }}
      w='100%'
    >
      <Text
        color='grey.700'
        fontSize={{ base: '0.875rem', md: '0.9375rem' }}
        fontStyle={{ base: 'normal', md: 'italic' }}
        letterSpacing={{ base: '-0.01em', md: 'normal' }}
        lineHeight={{ base: '1.4', md: '1.5' }}
      >
        {children}
      </Text>
    </Box>
  );
}

function QuoteStoryCard({ story }: { story: Extract<SuccessStory, { kind: 'quote' }> }) {
  const tokens = THEME_TOKENS[story.theme];

  return (
    <Stack
      bg={tokens.cardBg}
      border='1.33px solid'
      borderColor={tokens.cardBorder}
      borderRadius='12px'
      p={{ base: '16px', md: '24px' }}
      spacing={{ base: '16px', md: '24px' }}
    >
      <Stack p='8px' spacing='12px'>
        <Box h='32px' position='relative' w='48px'>
          <Image alt={story.flag.alt} sizes='48px' src={story.flag.src} style={{ objectFit: 'contain' }} fill />
        </Box>
        <Heading
          as='h3'
          color='grey.900'
          fontFamily='heading'
          fontSize={{ base: '1.375rem', md: '2.25rem' }}
          fontWeight='600'
          letterSpacing={{ base: 'normal', md: '-0.01em' }}
          lineHeight={{ base: '1.2', md: '1.1' }}
        >
          {story.heading}
        </Heading>
      </Stack>

      <Stack
        bg='white'
        border='1px solid'
        borderColor={tokens.quoteBorder}
        borderRadius={{ base: '8px', md: '6px' }}
        p={{ base: '16px', md: '24px' }}
        spacing='16px'
      >
        <Text
          color='grey.900'
          fontSize={{ base: '0.875rem', md: '1rem' }}
          fontStyle={{ base: 'normal', md: 'italic' }}
          letterSpacing={{ base: '-0.01em', md: 'normal' }}
          lineHeight={{ base: '1.4', md: '1.5' }}
        >
          {story.quote}
        </Text>
        <Flex alignItems='center' gap='12px' w='100%'>
          <Text
            color='grey.900'
            flexShrink={0}
            fontFamily='heading'
            fontSize={{ base: '1rem', md: '1.125rem' }}
            fontWeight='600'
            letterSpacing={{ base: 'normal', md: '-0.01em' }}
            lineHeight='1.1'
          >
            {story.attribution.name}
          </Text>
          <Box bg='grey.200' flex='1' h='1px' />
        </Flex>
        <Text color='grey.700' fontSize={{ base: '0.75rem', md: '0.8125rem' }} lineHeight='1.4' mt='-8px'>
          {story.attribution.title}
        </Text>
      </Stack>
    </Stack>
  );
}
